import { untrack } from 'solid-js';
import { rgb as d3rgb } from 'd3-color';
import { debug, error as logError } from './console';
import { getData } from './global';
import { apiEndpoints } from '@config/env';
import { persistantStore } from '../store/persistantStore';

/**
 * Global color scale utility for consistent coloring across all components
 * 
 * EVENT COLOR SCALE:
 *    - Uses unique colors for first 8 event selections
 *    - Defaults to blue for selections beyond index 8
 *    - Use for coloring based on event IDs
 *    - Example: createD3EventColorScale([1, 2, 3, 4])
 * 
 * FLEET SOURCES:
 *    - fetchSources() - Fetches sources with colors from the API
 *    - Components should create their own D3 ordinal scales from sources
 *    - Example: d3.scaleOrdinal().domain(sourceNames).range(colors)
 * 
 * USAGE EXAMPLES:
 * 
 * // Event Color Scale
 * const eventIds = [1, 2, 3];
 * const eventScale = createD3EventColorScale(eventIds);
 * const color1 = eventScale(1); // Returns first color
 * const color2 = eventScale(2); // Returns second color
 * 
 * - Ensures consistent color assignment across map, charts, and maneuver pages
 */

// D3 color schemes for the first 8 unique colors
const S20_COLORS = [
  '#1f77b4', // Blue
  '#ff7f0e', // Orange  
  '#2ca02c', // Green
  '#d62728', // Red
  '#9467bd', // Purple
  '#8c564b', // Brown
  '#e377c2', // Pink
  '#7f7f7f'  // Gray
];

export const MAX_INDEX_COLORS = 8;

export const DEFAULT_COLOR = '#1f77b4'; // Blue for selections beyond index 8

/**
 * Foreground text color (hex) for readable contrast on an arbitrary CSS background.
 * Uses sRGB relative luminance (WCAG-style). Accepts hex, rgb(), and common CSS color names.
 */
export function getContrastingTextOnBackground(backgroundCss: string): string {
  const c = d3rgb(backgroundCss);
  if (!Number.isFinite(c.r) || !Number.isFinite(c.g) || !Number.isFinite(c.b)) {
    return '#0f172a';
  }
  const linearize = (channel: number) => {
    const x = channel / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * linearize(c.r) + 0.7152 * linearize(c.g) + 0.0722 * linearize(c.b);
  return L > 0.45 ? '#0f172a' : '#ffffff';
}

export type CssSolidFillPair = { 'background-color': string; color: string };

/**
 * Pairs an arbitrary background with a readable foreground in one place (Chromium-safe).
 */
export function cssSolidFillPair(backgroundCss: string, options?: { important?: boolean }): CssSolidFillPair {
  const fg = getContrastingTextOnBackground(backgroundCss);
  if (options?.important) {
    return {
      'background-color': `${backgroundCss} !important`,
      color: `${fg} !important`,
    };
  }
  return {
    'background-color': backgroundCss,
    color: fg,
  };
}

/** First 7 S20 colors (no grey) - use for source fallback so no source gets grey when API returns no color */
const SOURCE_FALLBACK_COLORS = S20_COLORS.slice(0, 7);

/**
 * Deterministic color for a source name when API returns no color.
 * Same name always gets the same color across envs (dev/prod).
 */
export function getSourceFallbackColor(sourceName: string): string {
  if (!sourceName || typeof sourceName !== 'string') return DEFAULT_COLOR;
  let hash = 0;
  const str = sourceName.toLowerCase().trim();
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  const index = Math.abs(hash) % SOURCE_FALLBACK_COLORS.length;
  return SOURCE_FALLBACK_COLORS[index];
}

/**
 * Creates a consistent color scale for event IDs
 * @param eventIds - Array of unique event IDs to create colors for
 * @param maxUniqueColors - Maximum number of unique colors (default: 8)
 * @returns Object with domain (eventIds) and range (colors)
 */
export function createEventColorScale(eventIds: number[], maxUniqueColors: number = 8) {
  if (!eventIds || eventIds.length === 0) {
    debug('createEventColorScale: No event IDs provided');
    return { domain: [], range: [] };
  }

  // Use event IDs in the order they were provided (from selectedEvents order)
  // Don't sort them - the order matters for consistent color assignment
  const orderedEventIds = [...eventIds];
  
  // Create color range based on the order
  const colors = orderedEventIds.map((eventId, index) => {
    if (index < maxUniqueColors) {
      return S20_COLORS[index];
    } else {
      return DEFAULT_COLOR; // Blue for selections beyond index 8
    }
  });

  debug('createEventColorScale: Created color scale', {
    eventIds: orderedEventIds,
    colors: colors,
    maxUniqueColors,
    uniqueColorsUsed: Math.min(orderedEventIds.length, maxUniqueColors),
    defaultColorUsed: Math.max(0, orderedEventIds.length - maxUniqueColors)
  });

  return {
    domain: orderedEventIds,
    range: colors
  };
}

/**
 * Gets the color for a specific event ID
 * @param eventId - The event ID to get color for
 * @param eventIds - Array of all event IDs (for consistent ordering)
 * @param maxUniqueColors - Maximum number of unique colors (default: 8)
 * @returns Color string
 */
export function getEventColor(eventId: number, eventIds: number[], maxUniqueColors: number = 8): string {
  if (!eventIds || eventIds.length === 0) {
    return DEFAULT_COLOR;
  }

  // Use event IDs in the order they were provided (don't sort)
  const orderedEventIds = [...eventIds];
  const index = orderedEventIds.indexOf(eventId);
  
  if (index === -1) {
    return DEFAULT_COLOR;
  }

  if (index < maxUniqueColors) {
    return S20_COLORS[index];
  } else {
    return DEFAULT_COLOR;
  }
}

/**
 * Creates a D3 ordinal color scale for event IDs
 * @param eventIds - Array of unique event IDs
 * @param maxUniqueColors - Maximum number of unique colors (default: 8)
 * @returns D3 ordinal scale function
 */
export function createD3EventColorScale(eventIds: number[], maxUniqueColors: number = 8) {
  const colorScale = createEventColorScale(eventIds, maxUniqueColors);
  
  // Create a simple function that mimics D3's ordinal scale
  const scale = (eventId: number) => {
    return getEventColor(eventId, eventIds, maxUniqueColors);
  };
  
  // Add domain and range properties for compatibility
  scale.domain = colorScale.domain;
  scale.range = colorScale.range;
  
  return scale;
}

/**
 * Gets the color for a specific index (useful for consistent ordering)
 * @param index - The index (0-based)
 * @param maxUniqueColors - Maximum number of unique colors (default: 8)
 * @returns Color string
 */
export function getColorByIndex(index: number, maxUniqueColors: number = 8): string {
  if (index < maxUniqueColors) {
    return S20_COLORS[index];
  } else {
    return DEFAULT_COLOR;
  }
}

/**
 * Palette color for an event by its position in selectedEvents(), or undefined when it isn't selected.
 *
 * This is utils/global `getIndexColor` minus its 'lightgray' miss branch — callers here must be able to fall
 * through to another strategy rather than paint grey, and duplicating five lines avoids deepening the
 * existing global <-> colorScale import cycle. Keep the two in sync; a unit test asserts they agree.
 */
export function getSelectionIdentityColor(
  eventId: unknown,
  selectedEventIds?: readonly (number | string)[]
): string | undefined {
  if (!Array.isArray(selectedEventIds) || selectedEventIds.length === 0) return undefined;
  const target = Number(eventId);
  if (!Number.isFinite(target)) return undefined;
  const index = selectedEventIds.findIndex((value) => Number(value) === target);
  if (index < 0) return undefined;
  return selectedEventIds.length > MAX_INDEX_COLORS ? DEFAULT_COLOR : getColorByIndex(index);
}

/**
 * Stroke color for a map selection range; prefers the per-range color baked in from performance/maneuver
 * metadata, then the event's own index in selectedEvents(), and only then its position in selectedRanges().
 *
 * The positional fallback is a different index space from the one the tables color by, so a range that is
 * missing or out of order shifts every later maneuver's color. Passing selectedEventIds makes the color
 * identity-based and immune to that; it stays an optional argument so colorScale needs no store import.
 */
export function resolveSelectionRangeStrokeColor(
  range: { event_id?: number | string; color?: string; [key: string]: unknown },
  rangeIndex: number,
  totalRanges: number,
  selectedEventIds?: readonly (number | string)[]
): string {
  const explicit = range?.color;
  if (typeof explicit === "string" && explicit.trim() !== "") {
    return explicit;
  }
  const identity = getSelectionIdentityColor(range?.event_id, selectedEventIds);
  if (identity) {
    return identity;
  }
  return totalRanges > MAX_INDEX_COLORS ? DEFAULT_COLOR : getColorByIndex(rangeIndex);
}

/**
 * Debug utility to log color scale information
 * @param component - Name of the component using the color scale
 * @param eventIds - Array of event IDs
 * @param colorScale - The color scale object
 */
export function debugColorScale(component: string, eventIds: number[], colorScale: any) {
  debug(`${component}: Color scale created`, {
    eventIds: eventIds,
    domain: colorScale.domain,
    range: colorScale.range,
    maxUniqueColors: 8,
    uniqueColorsUsed: Math.min(eventIds.length, 8),
    defaultColorUsed: Math.max(0, eventIds.length - 8)
  });
}

// ============================================================================
// Fleet Sources Functions
// ============================================================================

/**
 * Source type from API
 */
export interface Source {
  source_id: number;
  source_name: string;
  color: string;
  fleet?: number;
  visible?: number;
}

// Cache for sources to prevent duplicate API calls
let sourcesCache: { key: string; sources: Source[]; timestamp: number } | null = null;
const CACHE_TTL = 60000; // 1 minute cache TTL

/**
 * Fetches sources from the API for the current project
 * Uses caching to prevent duplicate API calls
 * 
 * @deprecated Internal use only - Components should use sourcesStore instead of calling this directly
 * This function is kept for internal use by sourcesStore only.
 * 
 * @param className - Optional class name (if not provided, uses persistantStore)
 * @param projectId - Optional project ID (if not provided, uses persistantStore)
 * @param bypassCache - When true, skip the in-memory TTL cache (use after project settings add/remove source)
 * @returns Promise resolving to array of sources
 */
export async function fetchSources(
  className?: string,
  projectId?: number,
  bypassCache = false
): Promise<Source[]> {
  // Check if user is logged in before making API requests
  // Use dynamic import to avoid circular dependencies
  try {
    const { isLoggedIn } = await import('../store/userStore');
    if (!isLoggedIn()) {
      debug('fetchSources: User not logged in, skipping API request');
      return [];
    }
  } catch (err) {
    // If import fails, assume not logged in to avoid making unauthorized requests
    debug('fetchSources: Could not check login status, skipping API request');
    return [];
  }
  
  const { selectedClassName, selectedProjectId } = persistantStore;
  // Use provided values if available, otherwise read from store (wrapped in untrack to avoid reactive issues)
  // IMPORTANT: If className/projectId are explicitly provided (even if falsy), use them
  // Only fall back to store if they're undefined
  const finalClassName = className !== undefined ? className : untrack(() => selectedClassName());
  const finalProjectId = projectId !== undefined ? projectId : untrack(() => selectedProjectId());

  if (!finalClassName || !finalProjectId) {
    debug('fetchSources: Missing className or projectId', {
      className: finalClassName,
      projectId: finalProjectId
    });
    return [];
  }

  // Check cache first (skip when callers need a fresh list, e.g. sourcesStore.refresh(true))
  const cacheKey = `${finalClassName}:${finalProjectId}`;
  const now = Date.now();
  if (
    !bypassCache &&
    sourcesCache &&
    sourcesCache.key === cacheKey &&
    now - sourcesCache.timestamp < CACHE_TTL
  ) {
    debug('fetchSources: Using cached sources', {
      count: sourcesCache.sources.length,
      age: now - sourcesCache.timestamp
    });
    return sourcesCache.sources;
  }

  try {
    const response = await getData(
      `${apiEndpoints.app.sources}?class_name=${encodeURIComponent(finalClassName)}&project_id=${encodeURIComponent(finalProjectId)}`
    );

    if (response.success && response.data && Array.isArray(response.data)) {
      debug('fetchSources: Fetched sources from API', {
        count: response.data.length,
        sources: response.data.map((s: Source) => ({
          source_id: s.source_id,
          source_name: s.source_name,
          color: s.color,
          fleet: s.fleet,
          visible: s.visible
        }))
      });
      
      // Update cache
      sourcesCache = {
        key: cacheKey,
        sources: response.data,
        timestamp: now
      };
      
      return response.data;
    }

    debug('fetchSources: Invalid response structure', response);
    return [];
  } catch (error) {
    logError('❌ fetchSources: Error fetching sources', error);
    debug('fetchSources: Error fetching sources', error);
    return [];
  }
}


/**
 * Map display/uppercase and filter-object field names to normalized (lowercase) data keys.
 * Used when exact/key lookup fails so consumers with only lowercase data still get a value.
 */
const DISPLAY_TO_NORMALIZED: Record<string, string> = {
    RACE: 'race_number',
    RACE_NUMBER: 'race_number',
    LEG: 'leg_number',
    GRADE: 'grade',
    STATE: 'state',
    SELECTIONS: 'selection',
    CONFIG: 'config',
    CREW: 'crew',
    TESTS: 'tests',
    SOURCE_NAME: 'source_name',
    EVENT: 'event',
    YEAR: 'year',
    TACK: 'tack',
    // Filter-object names (e.g. from filters_dataset) -> normalized keys
    Race_number: 'race_number',
    Leg_number: 'leg_number',
    Grade: 'grade',
    State: 'state',
    Config: 'config',
    Crew: 'crew',
    Tests: 'tests',
    Event: 'event',
    Year: 'year',
    Source_name: 'source_name'
};

/** Legend / group name when many distinct races share one color */
export const RACE_NUMBER_COLLAPSED_LEGEND_NAME = 'RACING';

/**
 * True when Color By is race number (numeric races vs TRAINING). Supports `RACE` and legacy `race_number` / `RACE_NUMBER`.
 */
export function isRaceColorMode(color: string | undefined | null): boolean {
  if (color == null || color === '') return false;
  return color === 'RACE' || color === 'race_number' || color === 'RACE_NUMBER';
}

/** Training bucket: -1, TRAINING string, etc. */
export function isTrainingRaceMetadataValue(v: unknown): boolean {
  if (v === -1 || v === '-1') return true;
  if (v == null || v === '') return false;
  const s = String(v).trim().toLowerCase();
  return s === 'training';
}

/**
 * When distinct racing race numbers (not training) exceed this count, use one color for all races + grey for training.
 * ("More than 8 races" → collapse when count is 9+; individual colors when count &lt; 8.)
 */
export const RACE_NUMBER_COLLAPSE_DISTINCT_THRESHOLD = 8;

export function shouldCollapseRaceNumberColorGroups(distinctRacingCount: number): boolean {
  return distinctRacingCount > RACE_NUMBER_COLLAPSE_DISTINCT_THRESHOLD;
}

/**
 * Normalize Color By options for performance / fleet performance history: canonical `RACE`
 * placed immediately before `SELECTIONS`; collapse `race_number` / `RACE_NUMBER` / duplicate `RACE` into one `RACE` entry.
 */
export function mergePerformanceHistoryColorOptions(options: string[]): string[] {
  const RACE_ALIASES = new Set(['RACE', 'race_number', 'RACE_NUMBER']);
  const filtered = options.filter((o) => !RACE_ALIASES.has(o));
  const withRace = [...filtered];
  const selIdx = withRace.indexOf('SELECTIONS');
  if (!withRace.includes('RACE')) {
    if (selIdx >= 0) {
      withRace.splice(selIdx, 0, 'RACE');
    } else {
      withRace.push('RACE');
    }
  } else if (selIdx >= 0) {
    const ri = withRace.indexOf('RACE');
    if (ri >= 0 && ri !== selIdx - 1) {
      withRace.splice(ri, 1);
      const newSi = withRace.indexOf('SELECTIONS');
      withRace.splice(newSi >= 0 ? newSi : withRace.length, 0, 'RACE');
    }
  }
  const seen = new Set<string>();
  return withRace.filter((x) => (seen.has(x) ? false : (seen.add(x), true)));
}

/**
 * Color By DENSITY on the performance scatter: each point is coloured by how crowded its part of
 * the chart is (AdvancedScatter computes it per chart). It is not a data field, so pages build no
 * legend groups for it; the legend draws a Low → High ramp instead.
 */
export const PERFORMANCE_DENSITY_COLOR = 'DENSITY';

/**
 * Color By VMG on the performance history scatter: each point is coloured by its average VMG % of
 * target (Vmg_perc) on a blue (lowest) → red (highest) ramp, scaled per chart. Like DENSITY it is
 * computed by AdvancedScatter, so pages build no legend groups for it.
 */
export const PERFORMANCE_VMG_COLOR = 'VMG';

/** Color-by modes drawn as a per-chart ramp rather than categorical groups. */
export function isPerformanceRampColor(color: string | undefined | null): boolean {
  return color === PERFORMANCE_DENSITY_COLOR || color === PERFORMANCE_VMG_COLOR;
}

/**
 * Performance pages: DENSITY and SELECTIONS are client-side, so they are pinned last as
 * `DENSITY, SELECTIONS` (never duplicated, wherever the service or page put them).
 */
export function finalizePerformanceColorOptions(options: string[]): string[] {
  const seen = new Set<string>();
  const out = options.filter(
    (o) =>
      o !== 'SELECTIONS' &&
      o !== PERFORMANCE_DENSITY_COLOR &&
      o !== PERFORMANCE_VMG_COLOR &&
      (seen.has(o) ? false : (seen.add(o), true))
  );
  out.push(PERFORMANCE_DENSITY_COLOR, 'SELECTIONS');
  return out;
}

/** Performance history (source / fleet): RACE merge, then `..., VMG, DENSITY, SELECTIONS`. */
export function finalizePerformanceHistoryColorOptions(options: string[]): string[] {
  const out = finalizePerformanceColorOptions(mergePerformanceHistoryColorOptions(options));
  out.splice(out.indexOf(PERFORMANCE_DENSITY_COLOR), 0, PERFORMANCE_VMG_COLOR);
  return out;
}

/**
 * Helper to resolve data field value handling case sensitivity
 * @param item The data item object
 * @param field The field name to look for (e.g. 'GRADE', 'TACK', 'RACE')
 * @returns The value found or undefined
 */
/** Scatter points and the performance legend both use this for a missing or NONE group. */
export const EMPTY_GROUP_COLOR = 'lightgrey';

/** True when a color-by value is absent or the explicit NONE bucket. */
export function isEmptyGroupValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  const s = String(value).trim();
  return s === '' || s.toUpperCase() === 'NONE';
}

export const resolveDataField = (item: any, field: string): any => {
    if (!item || !field) return undefined;
    
    // 1. Try exact match (API returns fields in consistent case)
    if (item[field] !== undefined) return item[field];
    
    // 2. Try lowercase match (fallback for legacy data)
    const lowerField = field.toLowerCase();
    if (item[lowerField] !== undefined) return item[lowerField];
    
    // 3. Map display names to normalized field names (e.g. RACE -> race_number)
    const normalizedKey = DISPLAY_TO_NORMALIZED[field];
    if (normalizedKey !== undefined && item[normalizedKey] !== undefined) {
        return item[normalizedKey];
    }
    
    return undefined;
};
